import { Card, Form, Input, Select, Button, message, Tag, Empty, Pagination, Radio, Modal } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/request';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../language';
import './SupportPage.css';

const { TextArea } = Input;

interface Ticket {
  id: string;
  title: string;
  content: string;
  category: number;
  status: number;
  priority: number;
  createTime: string;
  updateTime: string;
}

interface TicketReply {
  id: string;
  ticketId: string;
  userId: string;
  adminId?: string;
  content: string;
  replyType: number;
  createTime: string;
}

const statusMap: Record<number, string> = {
  0: 'statusPending',
  1: 'statusProcessing',
  2: 'statusResolved',
  3: 'statusClosed',
};

const priorityColorMap: Record<number, string> = {
  1: 'green',
  2: 'blue',
  3: 'orange',
  4: 'red',
};

const priorityKeyMap: Record<number, string> = {
  1: 'priorityLow',
  2: 'priorityMedium',
  3: 'priorityHigh',
  4: 'priorityUrgent',
};

const categoryColorMap: Record<number, string> = {
  1: 'blue',
  2: 'purple',
  3: 'orange',
  4: 'green',
  5: 'cyan',
  6: 'default',
};

const categoryKeyMap: Record<number, string> = {
  1: 'categoryDrawing',
  2: 'category3DModel',
  3: 'categoryInvoice',
  4: 'categoryPayment',
  5: 'categorySDK',
  6: 'categoryOther',
};

const categoryOptions = [
  { label: 'categoryDrawing', value: 1 },
  { label: 'category3DModel', value: 2 },
  { label: 'categoryInvoice', value: 3 },
  { label: 'categoryPayment', value: 4 },
  { label: 'categorySDK', value: 5 },
  { label: 'categoryOther', value: 6 },
];

const CLOSED_STATUSES = [2, 3];

export default function SupportPage() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [replyForm] = Form.useForm();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);
  const [createVisible, setCreateVisible] = useState(false);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailTicket, setDetailTicket] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [hasAdminReplied, setHasAdminReplied] = useState(false);
  const [pendingReply, setPendingReply] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ records: Ticket[]; total: number }>(`/support/tickets?page=${current}&pageSize=10`);
      const data = (res as any).data || res as any;
      setTickets(data.records || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [current]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies, detailLoading]);

  const handleOpenDetail = async (ticket: Ticket) => {
    setDetailVisible(true);
    setDetailTicket(ticket);
    setReplies([]);
    setHasAdminReplied(false);
    setPendingReply(false);
    setDetailLoading(true);
    try {
      const res = await api.get<{ ticket: Ticket; replies: TicketReply[] }>(`/support/tickets/${ticket.id}`);
      const data = (res as any).data || res as any;
      const allReplies: TicketReply[] = data.replies || [];
      setReplies(allReplies);
      setDetailTicket(data.ticket || ticket);
      const lastReply = allReplies[allReplies.length - 1];
      setHasAdminReplied(lastReply?.replyType === 1);
      startAdminPoll(ticket.id, allReplies);
    } catch (error) {
      console.error('Failed to load ticket detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const stopAdminPoll = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startAdminPoll = (ticketId: string, currentReplies: TicketReply[]) => {
    stopAdminPoll();
    const latestAdminReplyId = currentReplies
      .filter(r => r.replyType === 1)
      .map(r => r.id)
      .pop();

    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await api.get<{ replies: TicketReply[] }>(`/support/tickets/${ticketId}/replies`);
        const raw = (res as any).data;
        const newReplies: TicketReply[] = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as any)?.replies)
          ? (raw as any).replies
          : [];
        if (newReplies.length > 0) {
          setReplies(newReplies);
        }

        if (latestAdminReplyId) {
          const hasNewAdminReply = newReplies.some(
            r => r.replyType === 1 && r.id !== latestAdminReplyId
          );
          if (hasNewAdminReply && pendingReply) {
            setPendingReply(false);
            const lastReply = newReplies[newReplies.length - 1];
            setHasAdminReplied(lastReply?.replyType === 1);
            stopAdminPoll();
          }
        } else {
          const lastReply = newReplies[newReplies.length - 1];
          setHasAdminReplied(lastReply?.replyType === 1);
        }
      } catch {
        // ignore polling errors
      }
    }, 5000);
  };

  const handleSubmitTicket = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await api.post('/support/tickets', values);
      message.success(t('menu.supportNamespace.ticketSubmitted'));
      form.resetFields();
      setCreateVisible(false);
      loadTickets();
    } catch (error) {
      message.error(t('menu.supportNamespace.ticketSubmitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!detailTicket || !hasAdminReplied) return;
    try {
      const values = await replyForm.validateFields();
      setReplySubmitting(true);
      const reply = await api.post<TicketReply>(`/support/tickets/${detailTicket.id}/replies`, values);
      const newReply: TicketReply = ((reply as any).data || reply) as TicketReply;
      setReplies(prev => [...prev, newReply]);
      replyForm.resetFields();
      setHasAdminReplied(false);
      setPendingReply(true);
      message.success(t('menu.supportNamespace.replySuccess'));
      startAdminPoll(detailTicket.id, [...replies, newReply]);
    } catch {
      message.error(t('menu.supportNamespace.replyFailed'));
    } finally {
      setReplySubmitting(false);
    }
  };

  const canReply = detailTicket ? !CLOSED_STATUSES.includes(detailTicket.status) : false;

  return (
    <div className="support-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('menu.supportNamespace.pageTitle')}</h1>
          <p className="text-gray-500 mt-1">{t('menu.supportNamespace.pageSubtitle')}</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateVisible(true)}
        >
          {t('menu.supportNamespace.newTicket')}
        </Button>
      </div>

      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">
            {t('menu.supportNamespace.totalTickets').replace('{count}', String(total))}
          </span>
          <Button icon={<ReloadOutlined />} onClick={loadTickets} loading={loading}>
            {t('menu.supportNamespace.refresh')}
          </Button>
        </div>
      </Card>

      {tickets.length === 0 && !loading ? (
        <Card>
          <Empty description={t('menu.supportNamespace.noTickets')}>
            <Button type="primary" onClick={() => setCreateVisible(true)}>
              {t('menu.supportNamespace.submitFirst')}
            </Button>
          </Empty>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tickets.map((item) => (
            <Card
              key={item.id}
              hoverable
              className="ticket-card"
              onClick={() => handleOpenDetail(item)}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-400 font-mono">#{item.id}</span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 truncate">{item.title}</h3>
                  </div>
                  <Tag color={item.status === 0 ? 'default' : item.status === 1 ? 'processing' : item.status === 2 ? 'success' : 'error'} className="flex-shrink-0 ml-2">
                    {t(`menu.supportNamespace.${statusMap[item.status]}`)}
                  </Tag>
                </div>

                <p className="text-gray-500 text-sm line-clamp-2 mb-3 flex-1">{item.content}</p>

                <div className="flex flex-wrap gap-2 mb-3">
                  {item.category && (
                    <Tag color={categoryColorMap[item.category]}>
                      {t(`menu.supportNamespace.${categoryKeyMap[item.category]}`)}
                    </Tag>
                  )}
                  <Tag color={priorityColorMap[item.priority]}>
                    {t(`menu.supportNamespace.${priorityKeyMap[item.priority]}`)}
                  </Tag>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                  <span>{t('menu.supportNamespace.created')}: {new Date(item.createTime).toLocaleDateString()}</span>
                  {item.updateTime !== item.createTime && (
                    <span>{t('menu.supportNamespace.updated')}: {new Date(item.updateTime).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {total > 10 && (
        <div className="flex justify-center mt-4">
          <Pagination
            current={current}
            total={total}
            pageSize={10}
            onChange={setCurrent}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* New Ticket Modal */}
      <Modal
        title={t('menu.supportNamespace.newTicketModalTitle')}
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        footer={null}
        width={560}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            label={t('menu.supportNamespace.title')}
            name="title"
            rules={[{ required: true, message: t('menu.supportNamespace.titlePlaceholder') }]}
          >
            <Input placeholder={t('menu.supportNamespace.titlePlaceholder')} size="large" />
          </Form.Item>
          <Form.Item
            label={t('menu.supportNamespace.category')}
            name="category"
            rules={[{ required: true, message: t('menu.supportNamespace.category') + ' *' }]}
          >
            <Radio.Group>
              {categoryOptions.map(opt => (
                <Radio.Button key={opt.value} value={opt.value} style={{ marginRight: 8 }}>
                  {t(`menu.supportNamespace.${opt.label}`)}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={t('menu.supportNamespace.priority')}
            name="priority"
            rules={[{ required: true, message: t('menu.supportNamespace.priority') + ' *' }]}
            initialValue={2}
          >
            <Select size="large" className="w-48">
              <Select.Option value={1}>{t('menu.supportNamespace.priorityLow')}</Select.Option>
              <Select.Option value={2}>{t('menu.supportNamespace.priorityMedium')}</Select.Option>
              <Select.Option value={3}>{t('menu.supportNamespace.priorityHigh')}</Select.Option>
              <Select.Option value={4}>{t('menu.supportNamespace.priorityUrgent')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={t('menu.supportNamespace.description')}
            name="content"
            rules={[{ required: true, message: t('menu.supportNamespace.descriptionPlaceholder') }]}
          >
            <TextArea
              rows={6}
              placeholder={t('menu.supportNamespace.descriptionPlaceholder')}
            />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setCreateVisible(false)}>{t('menu.supportNamespace.cancel')}</Button>
            <Button type="primary" onClick={handleSubmitTicket} loading={submitting}>
              {t('menu.supportNamespace.submit')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Ticket Detail Modal */}
      <Modal
        title={t('menu.supportNamespace.ticketDetail')}
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          stopAdminPoll();
        }}
        footer={null}
        width={560}
        styles={{ body: { height: 640, padding: 0 } }}
      >
        {detailTicket && (
          <div className="ticket-detail-wrapper">
            {/* Fixed Header */}
            <div style={{ padding: '4px 16px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-base font-semibold flex-1 mr-4 mb-0" style={{ wordBreak: 'break-word' }}>{detailTicket.title}</h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Tag color={categoryColorMap[detailTicket.category]}>
                  {t(`menu.supportNamespace.${categoryKeyMap[detailTicket.category]}`)}
                </Tag>
                <Tag color={priorityColorMap[detailTicket.priority]}>
                  {t(`menu.supportNamespace.${priorityKeyMap[detailTicket.priority]}`)}
                </Tag>
                <Tag color={detailTicket.status === 0 ? 'default' : detailTicket.status === 1 ? 'processing' : detailTicket.status === 2 ? 'success' : 'error'}>
                  {t(`menu.supportNamespace.${statusMap[detailTicket.status]}`)}
                </Tag>
                <span className="text-xs text-gray-400 ml-auto">
                  {t('menu.supportNamespace.created')}: {new Date(detailTicket.createTime).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Scrollable Chat Area */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '16px 4px' }}>
              {/* Initial ticket as first user bubble */}
              <div className="flex justify-end mb-3">
                <div className="ticket-bubble ticket-bubble-user rounded-lg p-3 text-sm" style={{ maxWidth: '85%' }}>
                  <div className="text-xs opacity-70 mb-1">
                    {t('menu.supportNamespace.userReply')}
                    &nbsp;&middot;&nbsp;
                    {new Date(detailTicket.createTime).toLocaleString()}
                  </div>
                  <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{detailTicket.content}</div>
                </div>
              </div>

              {/* Reply History */}
              {detailLoading ? (
                <div className="text-center text-gray-400 py-4">{t('menu.supportNamespace.loading')}</div>
              ) : replies.map((reply) => {
                const isUser = reply.replyType === 0;
                return (
                  <div key={reply.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
                    <div className={`ticket-bubble ${isUser ? 'ticket-bubble-user' : 'ticket-bubble-admin'} rounded-lg p-3 text-sm`} style={{ maxWidth: '85%' }}>
                      <div className="text-xs opacity-70 mb-1">
                        {isUser ? t('menu.supportNamespace.userReply') : t('menu.supportNamespace.adminReply')}
                        &nbsp;&middot;&nbsp;
                        {new Date(reply.createTime).toLocaleString()}
                      </div>
                      <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{reply.content}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Fixed Reply Footer */}
            <div className="ticket-detail-footer">
              {!canReply ? (
                <div className="text-center text-gray-400 py-2">{t('menu.supportNamespace.replyClosedTip')}</div>
              ) : pendingReply || !hasAdminReplied ? (
                <div className="text-center text-gray-400 py-2">{t('menu.supportNamespace.waitAdminReply')}</div>
              ) : (
                <Form form={replyForm} layout="vertical" onFinish={handleSubmitReply}>
                  <Form.Item
                    name="content"
                    rules={[{ required: true, message: t('menu.supportNamespace.replyPlaceholder') }]}
                  >
                    <TextArea rows={3} placeholder={t('menu.supportNamespace.replyPlaceholder')} />
                  </Form.Item>
                  <div className="flex justify-end">
                    <Button type="primary" htmlType="submit" loading={replySubmitting}>
                      {t('menu.supportNamespace.submitReply')}
                    </Button>
                  </div>
                </Form>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
